import { GlobalSettings } from "../global_settings.js";
import { CrashBreadcrumbs } from "./breadcrumbs.js";
import { CrashCheckpoints } from "./checkpoints.js";
import { CrashConfig } from "./crash_config.js";
import { CrashIdentity } from "./identity.js";
import { CrashStateSnapshot } from "./state_snapshot.js";
import { CRASH_FORMAT, type CrashCheckpoint, type CrashDeviceInfo, type CrashEvent, type CrashException, type CrashKind, type CrashMechanism, type CrashPlatform, type CrashSnapshot } from "./types.js";

export interface BuildOptions
{
  kind: CrashKind;
  handled: boolean;
  mechanism: CrashMechanism;
  error?: unknown;
  extra?: Record<string, unknown> | null;
  tags?: Record<string, string>;
  checkpoint?: CrashCheckpoint | null;
  snapshot?: CrashSnapshot | null;
  screen?: string | null;
  fingerprint?: string[] | null;
  dropped?: number;
}

let app_version: string | null = null;
let app_version_code: number | null = null;
let native_build_id: string | null = null;
let gpu_cache: string | null | undefined;
let screen_provider: () => string | null = () => null;

export class CrashEventBuilder
{
  public static setAppInfo( version: string | null, version_code: number | null, native_id: string | null ): void
  {
    app_version = version;
    app_version_code = version_code;
    native_build_id = native_id;
  }

  public static setScreenProvider( provider: () => string | null ): void
  {
    screen_provider = provider;
  }

  public static build( options: BuildOptions ): CrashEvent
  {
    const checkpoint = options.checkpoint === undefined ? CrashCheckpoints.current_checkpoint() : options.checkpoint;
    const snapshot = options.snapshot === undefined ? CrashStateSnapshot.current() : options.snapshot;
    const screen = options.screen === undefined ? screenName() : options.screen;
    const consent = CrashIdentity.isConsentAllowed();
    return {
      format: CRASH_FORMAT,
      event_id: makeEventId(),
      kind: options.kind,
      occurred_at: new Date().toISOString(),
      handled: options.handled,
      mechanism: options.mechanism,
      app: {
        version: app_version,
        version_code: app_version_code,
        build_id: CrashConfig.buildId(),
        native_build_id,
        env: GlobalSettings.ENV,
        is_prod: GlobalSettings.IS_PROD,
        platform: runtimePlatform(),
      },
      identity: CrashIdentity.get(),
      checkpoint: checkpoint ? { name: checkpoint.name, at: checkpoint.at } : null,
      screen,
      device: deviceInfo(),
      exception: options.error === undefined ? null : toException( options.error, 0 ),
      breadcrumbs: consent ? CrashBreadcrumbs.list() : [],
      snapshot: consent ? snapshot : null,
      tags: { checkpoint: checkpoint?.name ?? 'none', ...( options.tags ?? {} ) },
      fingerprint: options.fingerprint ?? null,
      extra: options.extra ?? null,
      dropped: options.dropped ?? 0,
    };
  }
}

function screenName(): string | null
{
  try { return screen_provider(); }
  catch { return null; }
}

function makeEventId(): string
{
  try
  {
    const id = globalThis.crypto?.randomUUID?.();
    if ( id ) return id;
  }
  catch {}
  return `${Date.now().toString( 16 )}-${Math.random().toString( 16 ).slice( 2, 10 )}-${Math.random().toString( 16 ).slice( 2, 10 )}`;
}

export function toException( value: unknown, depth: number ): CrashException | null
{
  if ( value == null ) return null;
  if ( value instanceof Error )
  {
    const cause = depth < CrashConfig.MAX_CAUSE_DEPTH ? toException( ( value as { cause?: unknown } ).cause, depth + 1 ) : null;
    return {
      type: value.name || 'Error',
      message: clip( value.message ?? '', CrashConfig.MAX_MESSAGE_CHARS ),
      stack: typeof value.stack === 'string' ? clip( value.stack, CrashConfig.MAX_STACK_CHARS ) : null,
      cause,
    };
  }
  if ( typeof value === 'object' )
  {
    const obj = value as { name?: unknown; message?: unknown; stack?: unknown };
    if ( typeof obj.message === 'string' )
    {
      return {
        type: typeof obj.name === 'string' && obj.name ? obj.name : 'ErrorLike',
        message: clip( obj.message, CrashConfig.MAX_MESSAGE_CHARS ),
        stack: typeof obj.stack === 'string' ? clip( obj.stack, CrashConfig.MAX_STACK_CHARS ) : null,
        cause: null,
      };
    }
    let text: string;
    try { text = JSON.stringify( value ); }
    catch { text = Object.prototype.toString.call( value ); }
    return { type: 'NonError', message: clip( text ?? 'null', CrashConfig.MAX_MESSAGE_CHARS ), stack: null, cause: null };
  }
  return { type: 'NonError', message: clip( String( value ), CrashConfig.MAX_MESSAGE_CHARS ), stack: null, cause: null };
}

function clip( text: string, max: number ): string
{
  return text.length > max ? `${text.slice( 0, max )}…` : text;
}

function deviceInfo(): CrashDeviceInfo
{
  const info: CrashDeviceInfo = {};
  try { info.user_agent = navigator.userAgent; } catch {}
  try { info.screen = [ window.screen.width, window.screen.height ]; } catch {}
  try { info.dpr = window.devicePixelRatio; } catch {}
  try { info.viewport = [ window.innerWidth, window.innerHeight ]; } catch {}
  try
  {
    const mem = ( performance as { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } } ).memory;
    info.js_heap_used_mb = mem ? Math.round( mem.usedJSHeapSize / 1048576 ) : null;
    info.js_heap_limit_mb = mem ? Math.round( mem.jsHeapSizeLimit / 1048576 ) : null;
  }
  catch {}
  try
  {
    const conn = ( navigator as { connection?: { effectiveType?: string; type?: string } } ).connection;
    info.network = conn?.type ?? conn?.effectiveType ?? null;
    info.online = navigator.onLine;
  }
  catch {}
  info.gpu = gpuName();
  return info;
}

export function gpuName(): string | null
{
  if ( gpu_cache !== undefined ) return gpu_cache;
  gpu_cache = null;
  try
  {
    const canvas = document.createElement( 'canvas' );
    const gl = ( canvas.getContext( 'webgl2' ) ?? canvas.getContext( 'webgl' ) ) as WebGLRenderingContext | null;
    if ( gl )
    {
      const ext = gl.getExtension( 'WEBGL_debug_renderer_info' );
      const renderer = ext ? gl.getParameter( ext.UNMASKED_RENDERER_WEBGL ) : gl.getParameter( gl.RENDERER );
      gpu_cache = typeof renderer === 'string' ? renderer : null;
      gl.getExtension( 'WEBGL_lose_context' )?.loseContext();
    }
  }
  catch {}
  return gpu_cache;
}

function runtimePlatform(): CrashPlatform
{
  const cap = ( window as any ).Capacitor;
  const value = cap?.getPlatform?.();
  return value === 'android' || value === 'ios' ? value : 'web';
}
